import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { eventsService, participantsService } from '../../services/firestoreService.js';
import { Calendar, Users, Search, Upload, Eye, Ban, CheckCircle2, Link as LinkIcon, QrCode, Download, Copy, Filter, ChevronDown } from 'lucide-react';
import ConfirmDialog from '../Common/ConfirmDialog';
import toast from 'react-hot-toast';

export const EventParticipants = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | disabled
  // Eliminado flujo de "Quitar". Usaremos toggle de bloqueo.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let unsub;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const ev = await eventsService.getEventById(id);
        setEvent(ev);
        unsub = participantsService.onEventParticipantsSnapshot(id, setParticipants);
      } catch (e) {
        console.error(e);
        setError('No se pudieron cargar los participantes');
      } finally {
        setLoading(false);
      }
    })();
    return () => { try { unsub && unsub(); } catch(_){} };
  }, [id]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return participants.filter(p => {
      const matches = (p.userName || p.userId || '').toLowerCase().includes(q);
      const isDisabled = p.canUploadPhotos === false || p.canViewPhotos === false || p.status === 'blocked';
      const statusOk = statusFilter === 'all' || (statusFilter === 'active' ? !isDisabled : isDisabled);
      return matches && statusOk;
    });
  }, [participants, search, statusFilter]);

  const isOnline = (p) => {
    const ts = p.lastActivity?.toDate ? p.lastActivity.toDate().getTime() : (p.lastActivity ? new Date(p.lastActivity).getTime() : 0);
    if (!ts) return false;
    return Date.now() - ts < 3 * 60 * 1000; // 3 minutos
  };

  const formatRelative = (date) => {
    if (!date) return '—';
    const d = date.toDate ? date.toDate() : new Date(date);
    const diff = Math.max(0, Date.now() - d.getTime());
    const s = Math.floor(diff/1000);
    if (s < 60) return `hace ${s}s`;
    const m = Math.floor(s/60);
    if (m < 60) return `hace ${m}m`;
    const h = Math.floor(m/60);
    return `hace ${h}h`;
  };

  const copyInviteLink = () => {
    if (!event?.joinCode) return;
    const url = `https://shot.muchpic.com/shot?code=${event.joinCode}`;
    try { navigator.clipboard.writeText(url); toast.success('Enlace copiado'); } catch(_) { toast.success('Enlace listo'); }
  };

  const copyJoinCode = () => {
    if (!event?.joinCode) return;
    try { navigator.clipboard.writeText(event.joinCode); toast.success('Código copiado'); } catch(_) { toast.success(`Código: ${event.joinCode}`); }
  };

  const getQrUrl = () => {
    const target = `https://shot.muchpic.com/shot?code=${encodeURIComponent(event?.joinCode || '')}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&margin=0&data=${encodeURIComponent(target)}`;
  };

  const downloadQr = async () => {
    const url = getQrUrl();
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error('fetch-failed');
      const blob = await res.blob();
      const obj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = obj;
      a.download = `qr-${event?.joinCode || 'evento'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(obj), 2000);
    } catch (_) {
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${event?.joinCode || 'evento'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {
        window.open(url, '_blank');
      }
    }
  };

  const toggleUpload = async (p) => {
    try {
      await participantsService.updateParticipant(p.participantId, { canUploadPhotos: !(p.canUploadPhotos !== false) });
      toast.success((p.canUploadPhotos !== false) ? 'Subidas deshabilitadas' : 'Subidas habilitadas');
    } catch(e) {
      console.error(e); toast.error('No se pudo actualizar');
    }
  };

  const toggleView = async (p) => {
    try {
      await participantsService.updateParticipant(p.participantId, { canViewPhotos: !(p.canViewPhotos !== false) });
      toast.success((p.canViewPhotos !== false) ? 'Visualización deshabilitada' : 'Visualización habilitada');
    } catch(e) { console.error(e); toast.error('No se pudo actualizar'); }
  };

  const toggleBlock = async (p) => {
    try {
      const isBlocked = p.status === 'blocked';
      await participantsService.updateParticipant(p.participantId, {
        status: isBlocked ? 'active' : 'blocked',
        // Opcional: al bloquear también impedir ver/subir
        ...(isBlocked ? { } : { canUploadPhotos: false, canViewPhotos: false })
      });
      toast.success(isBlocked ? 'Participante desbloqueado' : 'Participante bloqueado');
    } catch(e) { console.error(e); toast.error('No se pudo actualizar el estado'); }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Filtros */}
      <div className="bg-white p-3 border rounded mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por nombre o ID" className="w-full pl-9 pr-3 py-2 border rounded" />
        </div>
        <div className="relative w-full md:w-auto">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e)=>setStatusFilter(e.target.value)}
            className="appearance-none pl-9 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full md:w-[180px]"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="disabled">Restringidos</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-gray-600">No hay participantes todavía.</div>
      ) : (
        <div className="bg-white border rounded overflow-hidden">
          <div className="hidden sm:grid grid-cols-7 gap-2 p-2 text-xs font-semibold text-gray-700 border-b">
            <div>Usuario</div>
            <div>Ingreso</div>
            <div>Última actividad</div>
            <div>Subidas</div>
            <div>Subir</div>
            <div>Ver</div>
            <div>Acciones</div>
          </div>
          <div className="sm:hidden p-2 text-xs font-semibold text-gray-700 border-b">Listado</div>
          {filtered.map((p) => (
            <div key={p.participantId} className="border-b last:border-b-0">
              <div className="hidden sm:grid grid-cols-7 gap-2 p-2 text-sm">
                <div className="text-gray-900 truncate flex items-center gap-2">
                  {p.userPhotoURL ? (<img src={p.userPhotoURL} alt="" className="w-6 h-6 rounded-full object-cover" />) : (<span className={`inline-block w-2 h-2 rounded-full ${isOnline(p)?'bg-emerald-500':'bg-gray-400'}`} title={isOnline(p)?'En línea':'Offline'} />)}
                  <span className="truncate" title={p.userName || p.userId}>{p.userName || p.userId}</span>
                </div>
                <div className="text-gray-600">{p.joinedAt?.toDate ? new Date(p.joinedAt.toDate()).toLocaleString() : ''}</div>
                <div className="text-gray-600">{formatRelative(p.lastActivity)}</div>
                <div className="text-gray-700">{p.photosUploaded || 0}</div>
                <div className="text-gray-700 flex items-center gap-2">
                  <button onClick={()=>toggleUpload(p)} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${p.canUploadPhotos === false ? 'bg-gray-100 text-gray-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    <Upload className="w-3.5 h-3.5"/> {p.canUploadPhotos === false ? 'No' : 'Sí'}
                  </button>
                </div>
                <div className="text-gray-700 flex items-center gap-2">
                  <button onClick={()=>toggleView(p)} className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${p.canViewPhotos === false ? 'bg-gray-100 text-gray-700' : 'bg-indigo-50 text-indigo-700'}`}>
                    <Eye className="w-3.5 h-3.5"/> {p.canViewPhotos === false ? 'No' : 'Sí'}
                  </button>
                </div>
                <div className="text-gray-700 flex items-center gap-2">
                  {p.status === 'blocked' ? (
                    <button onClick={()=>toggleBlock(p)} className="px-2 py-1 rounded text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5"/>Desbloquear</button>
                  ) : (
                    <button onClick={()=>toggleBlock(p)} className="px-2 py-1 rounded text-xs bg-red-50 text-red-700 hover:bg-red-100 flex items-center gap-1"><Ban className="w-3.5 h-3.5"/>Bloquear</button>
                  )}
                </div>
              </div>
              <div className="sm:hidden p-3 text-sm">
                <div className="font-medium text-gray-900">{p.userName || p.userId}</div>
                <div className="text-gray-600">Ingreso: {p.joinedAt?.toDate ? new Date(p.joinedAt.toDate()).toLocaleString() : ''}</div>
                <div className="text-gray-600">Última act.: {formatRelative(p.lastActivity)}</div>
                <div className="text-gray-600">Subidas: {p.photosUploaded || 0}</div>
                <div className="mt-2 flex items-center gap-2">
                  <button onClick={()=>toggleUpload(p)} className={`px-2 py-1 rounded text-xs ${p.canUploadPhotos === false ? 'bg-gray-100 text-gray-700' : 'bg-emerald-50 text-emerald-700'}`}>Subir: {p.canUploadPhotos === false ? 'No' : 'Sí'}</button>
                  <button onClick={()=>toggleView(p)} className={`px-2 py-1 rounded text-xs ${p.canViewPhotos === false ? 'bg-gray-100 text-gray-700' : 'bg-indigo-50 text-indigo-700'}`}>Ver: {p.canViewPhotos === false ? 'No' : 'Sí'}</button>
                  {p.status === 'blocked' ? (
                    <button onClick={()=>toggleBlock(p)} className="px-2 py-1 rounded text-xs bg-emerald-50 text-emerald-700">Desbloquear</button>
                  ) : (
                    <button onClick={()=>toggleBlock(p)} className="px-2 py-1 rounded text-xs bg-red-50 text-red-700">Bloquear</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal QR */}
      {qrOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={()=>setQrOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-2xl p-5 w-[min(92vw,520px)]">
            <button onClick={()=>setQrOpen(false)} className="absolute top-2 right-2 p-1.5 rounded hover:bg-gray-100" aria-label="Cerrar">✕</button>
            <h3 className="text-lg font-semibold mb-3">QR de invitación</h3>
            <div className="text-sm text-gray-600 mb-4 break-all">https://shot.muchpic.com/shot?code={event?.joinCode}</div>
            <div className="flex items-center justify-center">
              <img src={getQrUrl()} alt={`QR para ${event?.joinCode}`} className="rounded-md border" style={{ width: 320, height: 320 }} />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button onClick={copyInviteLink} className="btn-secondary text-sm">Copiar enlace</button>
              <button onClick={downloadQr} className="btn-primary text-sm">Descargar QR</button>
            </div>
          </div>
        </div>
      )}

      {/* Sin confirm dialog para bloqueo/desbloqueo */}
    </div>
  );
};

export default EventParticipants;
